import { useMemo } from 'react';
import type { Study } from '../../api/types';
import { useUIStore } from '../../stores/uiStore';
import type { StudySummary } from './types';

/** Map a full Study to a non-PHI StudySummary for context injection. */
function toStudySummary(study: Study): StudySummary {
  return {
    objKey: study.objKey,
    modality: study.mod,
    status: study.status,
    date: study.date,
    description: study.descr,
    siteId: study.siteId,
  };
}

/**
 * Gather minimal non-PHI UI context for Roentgen context injection.
 *
 * Returns selected studies (stripped of PHI), active tab, and active filters.
 * This context is sent with every message to give the assistant awareness
 * of what the user is looking at.
 */
export function useRoentgenContext() {
  const selectedStudies = useUIStore((s) => s.selectedStudies);
  const activeTab = useUIStore((s) => s.activeTab);
  const activeFilters = useUIStore((s) => s.activeFilters);
  const advancedFilters = useUIStore((s) => s.advancedFilters);
  const quickSearch = useUIStore((s) => s.quickSearch);
  const quickSearchField = useUIStore((s) => s.quickSearchField);
  const sortModel = useUIStore((s) => s.sortModel);
  const resultLimit = useUIStore((s) => s.resultLimit);

  const studySummaries = useMemo(
    () => selectedStudies.map(toStudySummary),
    [selectedStudies],
  );

  return {
    selectedStudies: studySummaries,
    activeTab,
    activeFilters,
    advancedFilters,
    quickSearch,
    quickSearchField,
    sortModel,
    resultLimit,
  };
}
